import { Link } from 'react-router-dom';
import type { Capability } from '../../lib/types/auth';
import { ForbiddenRequestAffordance } from './ForbiddenRequestAffordance';
import { useSchoolContext } from '../SchoolContextProvider';
import { useSchoolDirectory } from '../SchoolDirectoryProvider';

const SUPPORT_MAILTO = 'mailto:itservicedesk@aurora-group.invalid?subject=UCP%20access%20request';

/**
 * In-shell "you don't have access" state for a role-gated route (the deny path of
 * `RouteGuard`). Replaces the previous silent `<Navigate to="/">` redirect, which
 * bounced an unentitled user to Comms Home with no explanation (they appeared to be
 * "defaulted to Communications"). This is UX / defence-in-depth only — the server
 * remains authoritative for every data call (capability + RLS, ADR-009/010).
 *
 * When the denied surface maps to a SELF-requestable capability (`requestCapability`
 * — e.g. `compose_send`, `complaint_record`, `atr_submit`) it offers the ADR-036
 * in-app "Request access" affordance, scoped to the active school. For surfaces
 * whose conferring role is appointment-only or governance-gated (HR roles, Insights,
 * Commercial, platform admin — deliberately NOT in `REQUESTABLE_ROLES`, PRO-0580
 * TD-3), it shows the IT Service Desk contact fallback instead — offering a request
 * the server would reject is a dead end.
 */
interface AccessDeniedViewProps {
  /** Human name of the surface the user tried to open, e.g. "People Assistant". */
  surfaceLabel?: string;
  /** `featureFlags.accessRequestEnabled` — gates the in-app request affordance. */
  accessRequestEnabled?: boolean;
  /** The capability the denied surface needs; drives the self-request affordance. */
  requestCapability?: Capability;
}

export function AccessDeniedView({
  surfaceLabel,
  accessRequestEnabled = false,
  requestCapability,
}: AccessDeniedViewProps) {
  const named = surfaceLabel ? `\u201C${surfaceLabel}\u201D` : 'this area';
  return (
    <section aria-labelledby="access-denied-heading" className="max-w-narrow">
      <h1 id="access-denied-heading">You don&rsquo;t have access to {named}</h1>
      <p>
        Your account doesn&rsquo;t have the role needed to open{' '}
        {surfaceLabel ?? 'this part of TAG Operations'}. Access is granted per role and school by a
        platform administrator.
      </p>
      {requestCapability ? (
        <div className="mt-4">
          <AccessDeniedRequestAffordance
            capability={requestCapability}
            accessRequestEnabled={accessRequestEnabled}
          />
        </div>
      ) : (
        <p className="text-sm text-text-secondary">
          To request access, contact the IT Service Desk:{' '}
          <a href={SUPPORT_MAILTO}>itservicedesk@aurora-group</a>.
        </p>
      )}
      <p className="mt-4">
        <Link to="/">Return to Home</Link>
      </p>
    </section>
  );
}

/**
 * Reads the active-school context (only mounted for the self-requestable routes, so
 * the school providers are always present) and delegates to the shared 403
 * affordance, which itself falls back to the service-desk contact when the feature
 * flag is off or the capability is not self-requestable.
 */
function AccessDeniedRequestAffordance({
  capability,
  accessRequestEnabled,
}: {
  capability: Capability;
  accessRequestEnabled: boolean;
}) {
  const { activeSchoolId } = useSchoolContext();
  const { activeSchoolName } = useSchoolDirectory();
  return (
    <ForbiddenRequestAffordance
      capability={capability}
      school={{ schoolId: activeSchoolId ?? '', name: activeSchoolName ?? 'this school' }}
      enabled={accessRequestEnabled && activeSchoolId !== null}
    />
  );
}
